import { useEffect, useState } from "react";
import api from "../lib/api";
import { Button } from "./ui/button";
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from "./ui/dialog";
import { Checkbox } from "./ui/checkbox";
import { Loader2, ShieldCheck, FileSignature } from "lucide-react";
import { toast } from "sonner";

/**
 * Blocking informativa dialog for the employee portal.
 *
 * On mount, fetches /delivery/consent. If required && !given, the modal becomes
 * mandatory (cannot be dismissed without accepting). Once accepted, calls
 * onAccepted() so the parent can re-render and continue.
 */
export const ConsentInformativaDialog = ({ onAccepted }) => {
    const [state, setState] = useState({ loading: true, required: false, given: false, text: "", version: "" });
    const [agreed, setAgreed] = useState(false);
    const [saving, setSaving] = useState(false);

    const fetchState = async () => {
        try {
            const { data } = await api.get("/delivery/consent");
            setState({ loading: false, ...data });
        } catch (e) {
            setState((s) => ({ ...s, loading: false }));
        }
    };

    useEffect(() => {
        fetchState();
    }, []);

    const handleAccept = async () => {
        if (!agreed) return;
        setSaving(true);
        try {
            await api.post("/delivery/consent", { version: state.version, accepted: true });
            toast.success("Informativa accettata — grazie!");
            setState((s) => ({ ...s, given: true }));
            onAccepted?.();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Impossibile registrare l'accettazione");
        } finally {
            setSaving(false);
        }
    };

    // Only render when consent IS required AND has NOT been given.
    const open = !state.loading && state.required && !state.given;

    return (
        <Dialog open={open} onOpenChange={() => { /* blocking — cannot dismiss */ }}>
            <DialogContent
                className="max-w-2xl [&>button.absolute]:hidden"
                data-testid="consent-informativa-dialog"
                onPointerDownOutside={(e) => e.preventDefault()}
                onEscapeKeyDown={(e) => e.preventDefault()}
            >
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2 font-display">
                        <ShieldCheck className="h-5 w-5 text-[#376DB3]" />
                        Informativa — Consegna telematica documenti
                    </DialogTitle>
                    <DialogDescription>
                        Prima di accedere ai tuoi documenti, ti chiediamo di leggere e accettare la presente informativa.
                    </DialogDescription>
                </DialogHeader>

                <div className="max-h-[50vh] overflow-y-auto rounded-md border border-slate-200 bg-slate-50 p-4 text-sm leading-relaxed text-slate-700 whitespace-pre-wrap" data-testid="consent-text">
                    {state.text || "Caricamento informativa…"}
                </div>

                <label className="flex items-start gap-2 rounded-md border border-blue-200 bg-blue-50 p-3 text-sm text-blue-900">
                    <Checkbox
                        checked={agreed}
                        onCheckedChange={(v) => setAgreed(!!v)}
                        className="mt-0.5"
                        data-testid="consent-checkbox"
                    />
                    <span>
                        Dichiaro di aver letto l'informativa e accetto la consegna telematica dei documenti di lavoro
                        attraverso il portale HR di Splendor Sud S.r.l. (versione {state.version || "1.0"}).
                    </span>
                </label>

                <DialogFooter>
                    <Button
                        onClick={handleAccept}
                        disabled={!agreed || saving}
                        className="gap-2 bg-[#376DB3] text-white hover:bg-[#376DB3]/90"
                        data-testid="consent-accept-btn"
                    >
                        {saving && <Loader2 className="h-4 w-4 animate-spin" />}
                        Accetto e continuo
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
};


/**
 * Acknowledgment dialog shown before downloading a document marked with
 * requires_acknowledgment=true. The actual download is triggered by the parent
 * via the onConfirm() callback once the user explicitly accepts.
 */
export const BulkAcknowledgmentDialog = ({ docs, open, onClose, onConfirm }) => {
    const [ackText, setAckText] = useState(null);
    const [agreed, setAgreed] = useState(false);
    const [busy, setBusy] = useState(false);

    useEffect(() => {
        let mounted = true;
        if (open) {
            setAgreed(false);
            api.get("/documents/ack-bulk/preview").then((r) => {
                if (mounted) setAckText(r.data.ack_text);
            }).catch(() => {
                if (mounted) setAckText(null);
            });
        }
        return () => { mounted = false; };
    }, [open]);

    const handleConfirm = async () => {
        if (!agreed) return;
        setBusy(true);
        try {
            await onConfirm();
            onClose();
        } finally {
            setBusy(false);
        }
    };

    return (
        <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
            <DialogContent className="max-w-lg" data-testid="bulk-ack-dialog">
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2 font-display">
                        <FileSignature className="h-5 w-5 text-amber-600" />
                        Presa visione di {docs?.length || 0} documenti
                    </DialogTitle>
                    <DialogDescription>
                        Conferma in un solo passaggio la presa visione di tutti i documenti elencati.
                    </DialogDescription>
                </DialogHeader>

                <div className="max-h-40 space-y-1 overflow-y-auto rounded-md border border-slate-200 bg-slate-50 p-2" data-testid="bulk-ack-doc-list">
                    {(docs || []).map((d) => (
                        <div key={d.id} className="flex items-center gap-2 truncate text-xs text-slate-700" data-testid={`bulk-ack-doc-${d.id}`}>
                            <span className="flex-shrink-0 rounded-full bg-[#376DB3]/10 px-2 py-0.5 text-[10px] font-semibold text-[#376DB3]">
                                {d.category_label}
                            </span>
                            <span className="truncate">{d.title || d.filename}</span>
                        </div>
                    ))}
                </div>

                <div className="rounded-md border border-amber-200 bg-amber-50 p-3 text-sm leading-relaxed text-amber-900" data-testid="bulk-ack-text">
                    {ackText || "Caricamento testo…"}
                </div>

                <label className="flex items-start gap-2 text-sm text-slate-700">
                    <Checkbox
                        checked={agreed}
                        onCheckedChange={(v) => setAgreed(!!v)}
                        className="mt-0.5"
                        data-testid="bulk-ack-checkbox"
                    />
                    <span>
                        Confermo di aver preso visione di tutti i documenti elencati e accetto che questa
                        azione venga registrata come ricevuta di consegna per ciascuno di essi.
                    </span>
                </label>

                <DialogFooter>
                    <Button variant="outline" onClick={onClose} data-testid="bulk-ack-cancel-btn">Annulla</Button>
                    <Button
                        onClick={handleConfirm}
                        disabled={!agreed || busy}
                        className="gap-2 bg-amber-600 text-white hover:bg-amber-700"
                        data-testid="bulk-ack-confirm-btn"
                    >
                        {busy && <Loader2 className="h-4 w-4 animate-spin" />}
                        Conferma presa visione
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
};


export const AcknowledgmentDialog = ({ doc, open, onClose, onConfirm }) => {
    const [meta, setMeta] = useState(null);
    const [agreed, setAgreed] = useState(false);
    const [busy, setBusy] = useState(false);

    useEffect(() => {
        let mounted = true;
        if (open && doc?.id) {
            setAgreed(false);
            setMeta(null);
            api.get(`/delivery/ack-preview/${doc.id}`).then((r) => {
                if (mounted) setMeta(r.data);
            }).catch(() => {
                if (mounted) setMeta(null);
            });
        }
        return () => { mounted = false; };
    }, [open, doc?.id]);

    const handleConfirm = async () => {
        if (!agreed) return;
        setBusy(true);
        try {
            await onConfirm();
            onClose();
        } finally {
            setBusy(false);
        }
    };

    return (
        <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
            <DialogContent className="max-w-lg" data-testid="ack-dialog">
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2 font-display">
                        <FileSignature className="h-5 w-5 text-amber-600" />
                        Presa visione richiesta
                    </DialogTitle>
                    <DialogDescription className="truncate font-mono text-xs" data-testid="ack-filename">
                        {doc?.filename}
                    </DialogDescription>
                </DialogHeader>

                <div className="rounded-md border border-amber-200 bg-amber-50 p-3 text-sm leading-relaxed text-amber-900" data-testid="ack-text">
                    {meta?.ack_text || "Caricamento testo…"}
                </div>

                <label className="flex items-start gap-2 text-sm text-slate-700">
                    <Checkbox
                        checked={agreed}
                        onCheckedChange={(v) => setAgreed(!!v)}
                        className="mt-0.5"
                        data-testid="ack-checkbox"
                    />
                    <span>
                        Confermo di aver preso visione del documento e accetto che questa azione venga
                        registrata come ricevuta di consegna.
                    </span>
                </label>

                <DialogFooter>
                    <Button variant="outline" onClick={onClose} data-testid="ack-cancel-btn">Annulla</Button>
                    <Button
                        onClick={handleConfirm}
                        disabled={!agreed || busy}
                        className="gap-2 bg-amber-600 text-white hover:bg-amber-700"
                        data-testid="ack-confirm-btn"
                    >
                        {busy && <Loader2 className="h-4 w-4 animate-spin" />}
                        Conferma e scarica
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
};
